/**
 * The wanted boards of the villages (ADR 0096): each day, a board offers
 * short bounties on a named creature at a named place of its region. A
 * bounty's target is the kin of one of the region's creatures, drawn with
 * its body and made stronger (`BOUNTY_SCALING`, `mobs.ts`), and it stands up
 * for the player who takes the bounty alone (and their party). Only what
 * names them lives here, so `mobs.ts` can read it without the biomes.
 */
export const WANTED_BOARD_MOB_ID = 'wanted-board';
/** Two a board, a day: drawn per character, never drawn again. */
export const BOUNTIES_PER_BOARD = 2;
/** A target nobody reached goes back where it came from: the bounty may be taken again. */
export const BOUNTY_LIFETIME_MS = 20 * 60_000;
/** How near the board one stands to read it, take a bounty or be paid. */
export const BOUNTY_REACH = 2;
/**
 * The bounties, by the id of their target. Each biome needs at least
 * `BOUNTIES_PER_BOARD` (test); more make the days differ.
 */
export const BOUNTY_MOB_IDS = [
    'bounty-fence-breaker',
    'bounty-hen-snatcher',
    'bounty-barn-gnawer',
    'bounty-berry-thief',
    'bounty-hoof-biter',
    'bounty-grey-fang',
    'bounty-bramble-heart',
    'bounty-smoking-stump',
    'bounty-axe-thief',
    'bounty-shoot-all',
    'bounty-gulp-all',
    'bounty-grave-scale',
    'bounty-boat-cruncher',
    'bounty-mad-lantern',
    'bounty-grave-wick',
    'bounty-blood-cackle',
    'bounty-nameless-warden',
    'bounty-camel-pincher',
    'bounty-tent-burner',
    'bounty-venom-spitter',
    'bounty-white-terror',
    'bounty-crystal-howler',
    'bounty-big-foot',
    'bounty-ice-beak',
    'bounty-bear-chaser',
];
export const BOUNTIES = Object.freeze({
    'bounty-fence-breaker': {
        biome: 'meadow',
        kin: 'boar',
        near: 'Butte aux Vents',
        name: 'Brise-Clôture',
        text: 'Un sanglier énorme défonce les clôtures au pied de la Butte aux Vents. Les fermiers de Havrebourg paient qui l’abattra.',
    },
    'bounty-hen-snatcher': {
        biome: 'meadow',
        kin: 'fox',
        near: 'Tour de Guet',
        name: 'Croque-Poules',
        text: 'Un renard rusé vide les poulaillers depuis des semaines. On l’a vu rôder près de la Tour de Guet.',
    },
    'bounty-barn-gnawer': {
        biome: 'meadow',
        kin: 'rat',
        near: 'La Ferme calcinée',
        name: 'Ronge-Grange',
        text: 'Un rat gros comme un chien a fait son nid dans la grange de la Ferme calcinée. Le fermier Gautier dit qu’il lui mange le peu que le feu a laissé.',
    },
    'bounty-berry-thief': {
        biome: 'meadow',
        kin: 'rat',
        near: 'Cercle des Anciens',
        name: 'Vole-Baies',
        text: 'Un rat énorme vole les baies que l’ermite Aubépin laisse au Cercle des Anciens. Aubépin dit que ses pierres sont très contrariées.',
    },
    'bounty-hoof-biter': {
        biome: 'meadow',
        kin: 'fox',
        near: 'Mont Clair',
        name: 'Mord-Sabot',
        text: 'Un grand renard mord les chevaux aux pattes au pied du Mont Clair. Le palefrenier Gaspard ne laisse plus sortir ses bêtes, et elles boudent.',
    },
    'bounty-grey-fang': {
        biome: 'forest',
        kin: 'wolf',
        near: 'Roc aux Loups',
        name: 'Croc-Gris',
        text: 'Un loup plus grand que les autres attaque les bûcherons près du Roc aux Loups. Sylvebois offre une prime pour sa peau.',
    },
    'bounty-bramble-heart': {
        biome: 'forest',
        kin: 'treant',
        near: 'Mont Moussu',
        name: 'Cœur-de-Ronce',
        text: 'Un sylvain furieux barre le sentier du Mont Moussu et écrase tout ce qui passe. Il faut l’abattre.',
    },
    'bounty-smoking-stump': {
        biome: 'forest',
        kin: 'treant',
        near: 'La Saignée',
        name: 'Souche-Fumante',
        text: 'Un sylvain à moitié brûlé tourne en rond dans la Saignée et assomme tout ce qui bouge. La druidesse Mélisande dit qu’il est de très mauvaise humeur, et on la croit.',
    },
    'bounty-axe-thief': {
        biome: 'forest',
        kin: 'goblin',
        near: 'Camp des Gobelins',
        name: 'Vole-Hache',
        text: 'Un gobelin a volé la hache d’Anselme le bûcheron et coupe du bois avec, près du Camp des Gobelins. Anselme dit surtout qu’il la tient mal.',
    },
    'bounty-shoot-all': {
        biome: 'forest',
        kin: 'goblin-archer',
        near: 'Pic des Ramures',
        name: 'Tire-Tout',
        text: 'Un archer gobelin perché sur le Pic des Ramures tire sur tout ce qui fait du bruit. Corvin le trappeur trouvait l’idée bonne, jusqu’à la flèche dans son chapeau.',
    },
    'bounty-gulp-all': {
        biome: 'swamp',
        kin: 'toad',
        near: 'Tertre Noyé',
        name: 'Gobe-Tout',
        text: 'Un crapaud géant avale les filets des pêcheurs au Tertre Noyé, et parfois les pêcheurs avec.',
    },
    'bounty-grave-scale': {
        biome: 'swamp',
        kin: 'lizardman',
        near: 'Cimetière Englouti',
        name: 'Écaille-Tombe',
        text: 'Un homme-lézard pille les tombes du Cimetière Englouti. Brumevase veut qu’on le chasse pour de bon.',
    },
    'bounty-boat-cruncher': {
        biome: 'swamp',
        kin: 'caiman',
        near: 'Ruines Noyées',
        name: 'Croque-Barque',
        text: 'Un caïman long comme une barque dort dans les Ruines Noyées et mord tout ce qui passe. Le passeur Ansgar refuse d’y emmener la sienne tant qu’il est là.',
    },
    'bounty-mad-lantern': {
        biome: 'swamp',
        kin: 'wisp',
        near: 'L’Empreinte',
        name: 'Lanterne-Folle',
        text: 'Un feu follet tourne sans arrêt au-dessus de l’Empreinte et attire les curieux dans la vase. La traqueuse Nella dit qu’il lui brouille toutes ses pistes.',
    },
    'bounty-grave-wick': {
        biome: 'swamp',
        kin: 'wisp',
        near: 'Cimetière Englouti',
        name: 'Mèche-Morte',
        text: 'Un feu follet se promène toutes les nuits entre les tombes du Cimetière Englouti. La fossoyeuse Guénola dit que ses morts n’arrivent plus à dormir avec cette lumière.',
    },
    'bounty-blood-cackle': {
        biome: 'desert',
        kin: 'hyena',
        near: 'Grotte des Hyènes',
        name: 'Ricane-Sang',
        text: 'Une hyène énorme attaque les caravanes près de la Grotte des Hyènes. Les chameliers paient bien.',
    },
    'bounty-nameless-warden': {
        biome: 'desert',
        kin: 'mummy',
        near: 'Obélisque Oublié',
        name: 'Gardien sans nom',
        text: 'Une momie s’est réveillée au pied de l’Obélisque Oublié. Elle frappe tous ceux qui approchent.',
    },
    'bounty-camel-pincher': {
        biome: 'desert',
        kin: 'scorpion',
        near: 'Relais des chameliers',
        name: 'Pince-Chameau',
        text: 'Un scorpion géant s’est caché sous le Relais des chameliers et pince les chameaux aux pattes. Idris dit que Safran ne veut plus s’agenouiller pour personne.',
    },
    'bounty-tent-burner': {
        biome: 'desert',
        kin: 'ember-salamander',
        near: 'Aiguille de Braise',
        name: 'Brûle-Tentes',
        text: 'Une salamandre de braise descend de l’Aiguille de Braise la nuit et met le feu aux tentes. Le forgeron de Sablerouge voudrait sa peau, vu le prix du charbon.',
    },
    'bounty-venom-spitter': {
        biome: 'desert',
        kin: 'cobra',
        near: 'La Plaine de verre',
        name: 'Crache-Venin',
        text: 'Un cobra cracheur vise les yeux des voyageurs sur la Plaine de verre. Kassim a juré par tous les puits de payer qui l’en débarrassera.',
    },
    'bounty-white-terror': {
        biome: 'snow',
        kin: 'polar-bear',
        near: "Caverne de l'Ours",
        name: 'Terreur-Blanche',
        text: 'Un ours blanc affamé est sorti de la Caverne de l’Ours. Les chasseurs de Givrecime n’osent plus y aller.',
    },
    'bounty-crystal-howler': {
        biome: 'snow',
        kin: 'ice-wolf',
        near: 'Cristaux du Givre',
        name: 'Hurle-Cristal',
        text: 'Un loup des glaces hurle chaque nuit autour des Cristaux du Givre. La voyante voudrait dormir.',
    },
    'bounty-big-foot': {
        biome: 'snow',
        kin: 'yeti',
        near: 'Crête du Givre',
        name: 'Grand-Pied',
        text: 'Un yéti boit la bière de l’éclaireur Torvald sur la Crête du Givre. Torvald jure qu’il n’en reste plus une goutte, et que ce n’est pas lui.',
    },
    'bounty-ice-beak': {
        biome: 'snow',
        kin: 'snow-harpy',
        near: 'La Brèche',
        name: 'Bec-de-Glace',
        text: 'Une harpie des neiges a fait son nid au bord de la Brèche et vole tout ce qui brille. Le forgeron de Givrecime y a perdu son meilleur marteau.',
    },
    'bounty-bear-chaser': {
        biome: 'snow',
        kin: 'frost-revenant',
        near: "Caverne de l'Ours",
        name: 'Chasse-Ours',
        text: 'Un revenant du givre s’est installé au fond de la Caverne de l’Ours, et même l’ours n’a pas voulu rester. La chasseuse Sigrun dit qu’elle irait bien, mais pas aujourd’hui.',
    },
});
const BOUNTY_IDS = new Set(BOUNTY_MOB_IDS);
export function isBountyMob(mobId) {
    return BOUNTY_IDS.has(mobId);
}
/** The bounties a biome's board may offer, in their order. */
export function bountiesOfBiome(biome) {
    return BOUNTY_MOB_IDS.filter((id) => BOUNTIES[id].biome === biome);
}
//# sourceMappingURL=bounties.js.map